import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Pagination, Spinner, Alert } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import api from '../../services/api';

const SearchResults = () => {
  const location = useLocation();
  const searchParams = location.state?.searchParams || {};

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    size: 12,
    total: 0
  });

  // Track if the component is mounted
  const [isMounted, setIsMounted] = useState(false);

  // Fetch results function defined outside useEffect to avoid recreation
  const fetchResults = async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      // Prepare query parameters
      const queryParams = new URLSearchParams();

      // Always add pagination parameters regardless of search params
      queryParams.append('page', pagination.page);
      queryParams.append('size', pagination.size);

      // If we have search parameters from location state, use them
      if (Object.keys(params).length > 0) {
        // Add basic filters
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.age_min) queryParams.append('age_min', params.age_min);
        if (params.age_max) queryParams.append('age_max', params.age_max);
        if (params.distance && params.distance !== 'any') queryParams.append('distance', params.distance);
        if (params.online) queryParams.append('online', params.online);
        if (params.photos) queryParams.append('photos', params.photos);
        if (params.keywords) queryParams.append('keywords', params.keywords);

        // Add location parameters
        if (params.latitude) queryParams.append('latitude', params.latitude);
        if (params.longitude) queryParams.append('longitude', params.longitude);
        if (params.city) queryParams.append('city', params.city);
        if (params.region) queryParams.append('region', params.region);
        if (params.country) queryParams.append('country', params.country);

        // Add multi-select filters
        params.sex?.forEach(value => queryParams.append('sex[]', value));
        params.orientation?.forEach(value => queryParams.append('orientation[]', value));
        params.looking_to?.forEach(value => queryParams.append('looking_to[]', value));
        params.eyes?.forEach(value => queryParams.append('eyes[]', value));
        params.body_type?.forEach(value => queryParams.append('body_type[]', value));
        params.ethnicity?.forEach(value => queryParams.append('ethnicity[]', value));
        params.income?.forEach(value => queryParams.append('income[]', value));
        params.net_worth?.forEach(value => queryParams.append('net_worth[]', value));
        params.children?.forEach(value => queryParams.append('children[]', value));
        params.relationship_status?.forEach(value => queryParams.append('relationship_status[]', value));
        params.smoker?.forEach(value => queryParams.append('smoker[]', value));
        params.drinker?.forEach(value => queryParams.append('drinker[]', value));
        params.pot_friendly?.forEach(value => queryParams.append('pot_friendly[]', value));
        params.party_drugs?.forEach(value => queryParams.append('party_drugs[]', value));

        // Add recent flag to save this search as the most recent search
        queryParams.append('recent', 'true');
      }

      // Fetch search results
      console.log('Fetching search results with params:', queryParams.toString());
      const response = await api.get(`/search?${queryParams.toString()}`);

      // Process the API response
      console.log('Search API response:', response.data);

      if (response.data && response.data.users) {
        // Set search results
        setResults(response.data.users);

        // Update pagination from API response
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            page: response.data.pagination.page || prev.page,
            size: response.data.pagination.size || prev.size,
            total: response.data.pagination.total || 0
          }));
        }

        // Log search parameters that were used
        if (response.data.search && response.data.search.filters) {
          console.log('Search filters used:', response.data.search.filters);
        }

        // Check if no results were found
        if (response.data.users.length === 0) {
          console.log('No search results found');
        }
      } else {
        // Handle unexpected response format
        console.error('Unexpected API response format:', response.data);
        setError('Received an unexpected response format from the server.');
        setResults([]);
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      setError('Failed to load search results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initial load effect - runs only once
  useEffect(() => {
    setIsMounted(true);

    // If we have search parameters from location state, use them
    if (Object.keys(searchParams).length > 0) {
      fetchResults(searchParams);
    } else {
      // If no search params, fetch results using the most recent search from the API
      fetchResults();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect for pagination changes
  useEffect(() => {
    // Skip the initial render
    if (isMounted) {
      if (Object.keys(searchParams).length > 0) {
        fetchResults(searchParams);
      } else {
        fetchResults();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      page
    }));

    // Scroll to top
    window.scrollTo(0, 0);
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(pagination.total / pagination.size);

    if (totalPages <= 1) return null;

    const items = [];

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(pagination.page - 1)}
        disabled={pagination.page === 1}
      />
    );

    // First page
    items.push(
      <Pagination.Item
        key={1}
        active={pagination.page === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );

    // Ellipsis if needed
    if (pagination.page > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    // Pages around current page
    for (let i = Math.max(2, pagination.page - 1); i <= Math.min(totalPages - 1, pagination.page + 1); i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={pagination.page === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Ellipsis if needed
    if (pagination.page < totalPages - 2) {
      items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
    }

    // Last page if not the first page
    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={pagination.page === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(pagination.page + 1)}
        disabled={pagination.page === totalPages}
      />
    );

    return (
      <Pagination className="justify-content-center">
        {items}
      </Pagination>
    );
  };

  if (loading && results.length === 0) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Searching for matches...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0">Search Results</h2>
        <Button as={Link} to="/search" variant="outline-primary">
          <i className="bi bi-sliders me-2"></i>
          Modify Search
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {results.length === 0 && !loading ? (
        <Card className="border-0 shadow-sm text-center p-5 mb-4">
          <Card.Body>
            <i className="bi bi-search display-1 text-muted mb-3"></i>
            <h3 className="fw-bold">No Matches Found</h3>
            <p className="text-muted mb-4">
              We couldn't find any profiles matching your search criteria.
              Try broadening your search parameters.
            </p>
            <Button as={Link} to="/search" variant="primary">
              Modify Search
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row>
            {results.map((user, index) => (
              <Col md={6} lg={4} key={index} className="mb-4">
                <Card className="user-card border-0 shadow-sm h-100">
                  <Card.Img
                    variant="top"
                    src={user.avatars?.medium}
                    alt={user.username}
                    style={{ height: '250px', objectFit: 'cover' }}
                  />
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Card.Title className="fw-bold mb-0">
                        {user.username}, {user.age || 'N/A'}
                      </Card.Title>
                      {user.online ? (
                        <Badge bg="success" className="px-2 py-1">Online</Badge>
                      ) : null}
                    </div>

                    <Card.Text className="text-muted mb-3">
                      {user.city && user.region ? (
                        <>
                          <i className="bi bi-geo-alt me-1"></i>
                          {user.city}, {user.region}
                        </>
                      ) : user.city ? (
                        <>
                          <i className="bi bi-geo-alt me-1"></i>
                          {user.city}
                        </>
                      ) : user.country ? (
                        <>
                          <i className="bi bi-geo-alt me-1"></i>
                          {user.country}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-geo-alt me-1"></i>
                          Location unknown
                        </>
                      )}
                    </Card.Text>

                    <div className="d-grid gap-2">
                      <Button
                        as={Link}
                        to={`/users/${user.username}`}
                        variant="primary"
                        size="sm"
                      >
                        View Profile
                      </Button>
                      <Button
                        as={Link}
                        to={`/messages/${user.username}`}
                        variant="outline-primary"
                        size="sm"
                      >
                        Send Message
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {renderPagination()}
        </>
      )}
    </Container>
  );
};

export default SearchResults;
